"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Check, ChevronDown, LoaderCircle, RefreshCw, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRunyiBackTrail } from "@/lib/use-runyi-back-trail";

export type GovernedWorkspace = "infrastructure" | "account";

type Approval = {
  id: string;
  tool_name: string;
  arguments: Record<string, unknown> | null;
  created_at: string;
  status: string;
  risk_level: string;
  approved_at: string | null;
  approved_by: string | null;
};

const reviewFields = [
  "operation", "action_key", "control_tower_action", "project_ref",
  "target_project_ref", "setup_id", "target_user_id",
  "email", "target_email", "account_email", "target_account_email",
] as const;

function readable(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function approvalDomain(action: Approval): GovernedWorkspace | "shared" {
  const values = action.arguments ?? {};
  const declared = String(values.governance_domain ?? values.workspace_domain ?? "").toUpperCase();
  if (declared === "ACCOUNT" || declared === "ACCOUNTS") return "account";
  if (declared === "INFRASTRUCTURE" || declared === "SERVICE" || declared === "RESOURCE") return "infrastructure";
  // The backend owns the operation schema. These common target references
  // provide a backwards-compatible classification, not a provider allowlist.
  if (values.project_ref || values.target_project_ref || values.setup_id) return "infrastructure";
  if (values.email || values.target_email || values.account_email || values.target_account_email) return "account";
  return "shared"; // Never hide an unclassified protected action from Owner review.
}

/** Two mobile workspaces, one Owner Root action register and decision service. */
export function EnterpriseApprovalWorkspace({
  supabase, connected, scope,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  scope: GovernedWorkspace;
}) {
  const [actions, setActions] = useState<Approval[]>([]);
  const [canApprove, setCanApprove] = useState(false);
  const [opened, setOpened] = useState(false);
  useRunyiBackTrail(opened, setOpened);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [promptAction, setPromptAction] = useState<Approval | null>(null);
  const [promptDecision, setPromptDecision] = useState<"approve" | "reject" | null>(null);
  const automaticallyShown = useRef<string | null>(null);
  const panel = useRef<HTMLElement | null>(null);

  const load = useCallback(async () => {
    if (!connected || !supabase) {
      setActions([]);
      setCanApprove(false);
      setErrorMessage(connected ? "Owner Root connection is unavailable." : null);
      return;
    }
    setLoading(true);
    try {
      const [{ data: account, error: accountError }, { data, error }] = await Promise.all([
        supabase.rpc("runyi_my_account"),
        supabase.from("ai_tool_actions")
          .select("id,tool_name,arguments,status,risk_level,created_at,approved_at,approved_by")
          .eq("status", "pending_approval").eq("risk_level", "protected")
          .is("approved_at", null).is("approved_by", null)
          .order("created_at", { ascending: false }).limit(100),
      ]);
      if (accountError) throw accountError;
      if (error) throw error;
      const pending = (data ?? []) as Approval[];
      const ownerCanApprove = account?.can_approve === true;
      setCanApprove(ownerCanApprove);
      setActions(pending);
      setPromptAction((current) => current && !pending.some((action) => action.id === current.id) ? null : current);
      // Present a real in-app review for a newly arrived fixed member repair.
      // No approval is made by this effect; only a confirmed Owner press may decide it.
      const freshRepair = scope === "infrastructure" && ownerCanApprove
        ? pending.find((action) => [
            "m4_member_extension_repair",
            "m4_member_health_repair",
            "m4_member_session_recovery",
          ].includes(action.tool_name) && approvalDomain(action) === "infrastructure") : null;
      if (freshRepair && automaticallyShown.current !== freshRepair.id) {
        automaticallyShown.current = freshRepair.id;
        setOpened(true);
        setPromptDecision(null);
        setPromptAction(freshRepair);
      }
      setErrorMessage(null);
    } catch (error) {
      setActions([]); // An unsuccessful refresh never leaves stale Approve controls.
      setCanApprove(false);
      setErrorMessage(error instanceof Error ? error.message : "Unable to load Owner Root approvals.");
    } finally {
      setLoading(false);
    }
  }, [connected, supabase, scope]);

  useEffect(() => {
    void load();
    if (!connected || !supabase) return;
    const timer = window.setInterval(() => void load(), 8000);
    const foreground = () => { if (!document.hidden) void load(); };
    document.addEventListener("visibilitychange", foreground);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", foreground);
    };
  }, [connected, supabase, load]);

  useEffect(() => {
    const eventName = scope === "account"
      ? "runyi:show-account-approvals"
      : "runyi:show-infrastructure-approvals";
    const open = () => {
      setOpened(true);
      void load();
      window.requestAnimationFrame(() => panel.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    };
    window.addEventListener(eventName, open);
    return () => window.removeEventListener(eventName, open);
  }, [load, scope]);

  const visible = useMemo(() => actions.filter((action) => {
    const domain = approvalDomain(action);
    const matchesScope = domain === scope || domain === "shared";
    const needle = search.trim().toLowerCase();
    if (!matchesScope) return false;
    if (!needle) return true;
    return [action.tool_name, ...reviewFields.map((key) => String(action.arguments?.[key] ?? ""))]
      .join(" ").toLowerCase().includes(needle);
  }), [actions, scope, search]);

  async function decide(action: Approval, decision: "approve" | "reject", confirmed = false) {
    if (!supabase || !connected || !canApprove || busyId) return;
    if (!confirmed) {
      setPromptAction(action);
      setPromptDecision(decision);
      return;
    }
    if (promptAction?.id !== action.id || promptDecision !== decision) return;
    setBusyId(action.id);
    setNotice(null);
    let failure: string | null = null;
    try {
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean; status?: string; tool_name?: string; error?: string;
      }>("runyi-ai", { body: { action: "decide_approval", approval_id: action.id, decision } });
      if (error || data?.error || data?.ok !== true) {
        let detail = data?.error ?? error?.message ?? "Owner Root did not confirm the decision.";
        const context = (error as { context?: Response } | null)?.context;
        if (context) {
          const response = await context.clone().json().catch(() => null) as { error?: string } | null;
          if (response?.error) detail = response.error;
        }
        throw new Error(detail);
      }
      setNotice(decision === "reject"
        ? "Protected operation rejected by the authorized RUNYI operator."
        : `${readable(data?.tool_name ?? action.tool_name)}: ${data?.status ?? "completed"}.`);
    } catch (error) {
      failure = error instanceof Error ? error.message : "The protected decision failed.";
    } finally {
      setBusyId(null);
      setPromptDecision(null);
      setPromptAction(null);
      setOpened(true);
      await load();
      if (failure) setErrorMessage(failure);
    }
  }

  useEffect(() => {
    if (!promptAction) return;
    const closeTop = () => {
      if (busyId) return;
      if (promptDecision) setPromptDecision(null);
      else setPromptAction(null);
    };
    const onBack = (event: Event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closeTop();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeTop();
    };
    // Capture before the generic in-workspace Back trail consumes the frame.
    window.addEventListener("runyi:request-back", onBack, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("runyi:request-back", onBack, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [promptAction, promptDecision, busyId]);

  const closePrompt = () => {
    if (busyId) return;
    setPromptAction(null);
    setPromptDecision(null);
  };

  return (
    <>
    <section ref={panel} data-runyi-owner-root-approvals={scope} className="w-full min-w-0 scroll-mt-24 border-b border-foreground/10 bg-background/95 px-2 py-1.5 sm:rounded-xl sm:border sm:px-4">
      <button type="button" onClick={() => setOpened((value) => !value)}
        className="flex min-h-9 w-full items-center gap-2 text-left" aria-expanded={opened}>
        <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-amber-400/10 text-amber-500"><ShieldCheck className="size-4" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold">Protected approvals</span>
          <span className="hidden text-[11px] text-foreground/50 sm:block">OWNER ROOT · {scope === "account" ? "Enterprise accounts" : "Infrastructure and services"}</span>
        </span>
        <span className="rounded-full bg-amber-400/12 px-2 py-1 text-xs font-semibold text-amber-500">{visible.length}</span>
        <ChevronDown className={`size-4 shrink-0 transition ${opened ? "rotate-180" : ""}`} />
      </button>
      {opened && (
        <div className="space-y-3 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <input aria-label="Search protected actions" placeholder="Search action, project or account…"
              className="h-11 min-w-0 flex-1 rounded-xl border border-foreground/10 bg-background px-3 text-sm outline-none focus:border-primary"
              value={search} onChange={(event) => setSearch(event.target.value)} />
            <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading} className="h-11 rounded-xl">
              <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
          {errorMessage && <p role="alert" className="break-words rounded-xl border border-red-500/20 p-3 text-xs text-red-500">{errorMessage}</p>}
          {notice && <p role="status" className="flex items-start gap-2 rounded-xl border border-lime-500/20 p-3 text-xs"><Check className="size-4 shrink-0 text-lime-500" />{notice}</p>}
          {!loading && !errorMessage && visible.length === 0 &&
            <p className="rounded-xl bg-foreground/[0.035] px-3 py-4 text-sm text-foreground/55">No undecided protected actions in this workspace.</p>}
          {visible.map((action) => (
            <article key={action.id} className="min-w-0 space-y-3 rounded-xl border border-amber-400/20 bg-amber-400/[0.04] p-3">
              <div>
                <p className="break-words text-sm font-semibold">{readable(action.tool_name)}</p>
                <p className="mt-1 text-[11px] text-foreground/50">
                  {approvalDomain(action) === "shared" ? "Shared governance" : readable(approvalDomain(action))} · {readable(action.risk_level)} · {new Date(action.created_at).toLocaleString()}
                </p>
              </div>
              <div className="space-y-1 rounded-xl bg-background/70 p-3 text-xs">
                {reviewFields.filter((key) => action.arguments?.[key] != null).map((key) => (
                  <p key={key} className="flex flex-wrap gap-2">
                    <span className="text-foreground/50">{readable(key)}:</span>
                    <span className="min-w-0 break-all font-medium">{String(action.arguments?.[key]).slice(0, 240)}</span>
                  </p>
                ))}
                <p className="break-all text-[10px] text-foreground/40">Approval ID: {action.id}</p>
              </div>
              {canApprove ? (
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" className="min-h-11 rounded-xl" disabled={Boolean(busyId)} onClick={() => void decide(action, "approve")}>
                    {busyId === action.id ? <LoaderCircle className="size-4 animate-spin" /> : <Check className="size-4" />}Approve
                  </Button>
                  <Button type="button" variant="outline" className="min-h-11 rounded-xl" disabled={Boolean(busyId)} onClick={() => void decide(action, "reject")}>
                    <X className="size-4" />Reject
                  </Button>
                </div>
              ) : <p className="text-xs text-amber-500">Only an account authorized by OWNER ROOT can decide protected operations.</p>}
            </article>
          ))}
        </div>
      )}
    </section>
    {promptAction && typeof document !== "undefined" && createPortal(
      <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/75 px-2 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:items-center"
        role="presentation">
        <div role="dialog" aria-modal="true" aria-labelledby="runyi-protected-approval-title"
          className="max-h-full w-full max-w-lg overflow-y-auto rounded-2xl border border-amber-400/30 bg-background p-4 text-foreground shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-500">OWNER ROOT · Protected action</p>
              <h2 id="runyi-protected-approval-title" className="mt-1 break-words text-lg font-semibold">{readable(promptAction.tool_name)}</h2>
            </div>
            <button type="button" aria-label="Close approval review" onClick={closePrompt} disabled={Boolean(busyId)}
              className="grid size-11 shrink-0 place-items-center rounded-xl border border-foreground/10"><X className="size-5" /></button>
          </div>
          <div className="mt-4 space-y-2 rounded-xl border border-foreground/10 bg-foreground/[0.035] p-3 text-sm">
            {reviewFields.filter((key) => promptAction.arguments?.[key] != null).map((key) => (
              <p key={key} className="flex flex-wrap gap-x-2 gap-y-1">
                <span className="text-foreground/50">{readable(key)}:</span>
                <span className="min-w-0 break-all font-medium">{String(promptAction.arguments?.[key]).slice(0, 240)}</span>
              </p>
            ))}
            {promptAction.arguments?.member_email != null && <p className="break-all">Account: {String(promptAction.arguments.member_email)}</p>}
            <p className="break-all text-[11px] text-foreground/50">Approval ID: {promptAction.id}</p>
          </div>
          {promptAction.tool_name === "m4_member_extension_repair" && (
            <p className="mt-3 rounded-xl border border-amber-500/20 p-3 text-xs leading-5 text-foreground/70">
              This fixed operation repairs the project’s PostgreSQL extension schemas. It does not replay the already installed Control Tower v4 bootstrap or alter the Owner Root.
            </p>
          )}
          {!canApprove ? (
            <p className="mt-4 text-sm text-amber-500">Only the authorized OWNER ROOT account can approve or reject this action.</p>
          ) : promptDecision ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm font-medium">Confirm {promptDecision} for this exact project and operation?</p>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" className="min-h-12 rounded-xl" disabled={Boolean(busyId)}
                  onClick={() => void decide(promptAction, promptDecision, true)}>
                  {busyId ? <LoaderCircle className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
                  Confirm {readable(promptDecision)}
                </Button>
                <Button type="button" variant="outline" className="min-h-12 rounded-xl" disabled={Boolean(busyId)}
                  onClick={() => setPromptDecision(null)}>Back</Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button type="button" className="min-h-12 rounded-xl" disabled={Boolean(busyId)}
                onClick={() => setPromptDecision("approve")}><Check className="size-4" />Approve</Button>
              <Button type="button" variant="outline" className="min-h-12 rounded-xl" disabled={Boolean(busyId)}
                onClick={() => setPromptDecision("reject")}><X className="size-4" />Reject</Button>
            </div>
          )}
        </div>
      </div>,
      document.body,
    )}
    </>
  );
}
